// Scoring rules: slicing tickets, combos (batches), critical hits, the parachute cascade,
// the Duplicate-Storm mash, life restoration on score milestones, and game-over.
import { G } from './state';
import { CRIT_CHANCE, CRIT_PTS, POM_AIRBORNE_MUL, POM_CAP, POM_PTS, PRIO, ROUND } from './config';
import type { Entity, Prio } from './types';
import { addText, cardHalves, shred } from '../fx/effects';
import { buzz, sfx } from '../fx/audio';
import { showEnd } from '../ui/screens';

export function addScore(p: number): void {
  G.score += p;
  while (G.score >= G.milestone) {
    if (G.missed > 0) {
      G.missed--;
      addText(G.W / 2, G.H * 0.22, 'SLA restored ✓', '#0369a1', true);
    }
    G.milestone += 100;
  }
}

export function resolveTicket(o: Entity, viaChute: boolean): void {
  o.dead = true;
  G.resolved++;
  const prio = o.prio as Prio;
  let pts = PRIO[prio].pts;
  addText(o.x, o.y - o.r, '✓ Resolved  +' + pts, '#15803d');
  if (!viaChute && Math.random() < CRIT_CHANCE) {
    pts += CRIT_PTS;
    addText(o.x, o.y - o.r - 28, 'FIRST-TOUCH RESOLUTION! +' + CRIT_PTS, '#b45309', true);
    sfx.crit();
  }
  cardHalves(o, PRIO[prio].color);
  shred(o.x, o.y, '#cfd3da', 10);
  shred(o.x, o.y, PRIO[prio].color, 8);
  addScore(pts);
  if (!viaChute) {
    G.comboBuf.push(PRIO[prio].pts);
    G.comboTimer = 0.18;
  }
  sfx.slice();
  buzz(12);
}

export function flushCombo(): void {
  if (G.comboBuf.length >= 3) {
    const bonus = G.comboBuf.reduce((a, b) => a + b, 0);
    addScore(bonus);
    G.bestBatch = Math.max(G.bestBatch, G.comboBuf.length);
    addText(G.W / 2, G.H * 0.3, 'BATCH RESOLVED ×' + G.comboBuf.length + '  +' + bonus, '#16a34a', true);
    sfx.combo();
    buzz(30);
  }
  G.comboBuf = [];
}

export function sliceChute(o: Entity): void {
  o.dead = true;
  sfx.chute();
  buzz([20, 40, 20]);
  G.shake = 0.35;
  addText(G.W / 2, G.H * 0.42, 'PARAHELP TAKES THE QUEUE', '#16a34a', true);
  let n = 0;
  for (const q of G.objs) {
    if (q.dead) continue;
    if (q.kind === 'ticket') {
      q.dead = true;
      G.resolved++;
      n++;
      addScore(PRIO[q.prio as Prio].pts);
      cardHalves(q, PRIO[q.prio as Prio].color);
      addText(q.x, q.y, '✓', '#16a34a');
    } else if (q.kind === 'angry') {
      q.happy = true;
      q.vy = Math.min(q.vy, -3);
      q.vr = 0;
      addText(q.x, q.y - q.r, 'customer calmed ♥', '#db2777');
    }
  }
  if (n > 0) {
    addScore(20);
    addText(G.W / 2, G.H * 0.42 + 42, 'AUTO-RESOLVED ×' + n + '  +20', '#15803d');
  }
  G.frenzyUntil = G.t + 3;
}

export function startMash(o: Entity): void {
  o.dead = true;
  flushCombo();
  // Divide the airborne enlargement back out so the zoomed-in mash stays its original size.
  G.mash = { x: o.x, y: o.y, r: o.r / POM_AIRBORNE_MUL, hits: 0, t: 0, zoom: 0 };
  addText(G.W / 2, G.H * 0.2, 'DUPLICATE STORM!', '#8e4ec6', true);
  sfx.chute();
  buzz(30);
}

// Where the storm sprite sits mid-zoom (lerped from its slice point to screen centre).
export function mashCenter(): { x: number; y: number; r: number } {
  const m = G.mash!;
  const z = m.zoom;
  return {
    x: m.x + (G.W / 2 - m.x) * z,
    y: m.y + (G.H * 0.42 - m.y) * z,
    r: m.r * (1 + z * 1.3),
  };
}

export function gameOver(reason: NonNullable<typeof G.deathBy>): void {
  G.deathBy = reason;
  sfx.boom();
  G.shake = 0.8;
  buzz(200);
  setTimeout(() => showEnd(), 700);
  G.state = 'dead';
}

// ----- end-screen copy -----
// The score-based title + subtitle. This is what the RESULT TICKET always shows, regardless
// of how the round ended. (Six-rung ladder matching the six ticket art variants.)
export function scoreTitle(): [string, string] {
  if (G.score < 100) return ['Support Intern', 'you replied "looking into it" and logged off'];
  if (G.score < 250) return ['Ticket Wrangler', 'the queue fears you, a little'];
  if (G.score < 450) return ['Queue Crusher', 'inbox trembles at your name'];
  if (G.score < 700) return ['SLA Samurai', 'honor. discipline. sub-second resolution.'];
  if (G.score < 1000) return ['Senior Ticket Ninja', 'promoted. again.'];
  return ['Zero-Inbox Zen Master', 'the queue is empty. the mind is clear.'];
}

// The PAGE headline + subtitle. Angry-customer death overrides with "Escalated to Legal";
// an SLA-breach death keeps the earned title but prefixes the queue-won line (spec §page).
export function pageHeadline(): [string, string] {
  if (G.deathBy === 'angry') return ['Escalated to Legal', 'you sliced an angry customer. bold strategy.'];
  const [t, s] = scoreTitle();
  if (G.deathBy === 'sla') return [t, '3 SLA breaches — the queue has won. ' + s];
  return [t, s];
}

export function csat(): number {
  return Math.min(99, Math.round(62 + G.score / 25 - G.breaches * 4));
}
